import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

interface TokenRefreshOptions {
    accessToken: string | null;
    refreshToken: string | null;
    onTokenRefreshed: (newAccessToken: string) => void;
    onRefreshFailed: () => void;
}

/**
 * Custom hook to automatically refresh access tokens before they expire
 * Access tokens expire in 15 minutes, this refreshes them at 14 minutes
 */
export function useTokenRefresh({
    accessToken,
    refreshToken,
    onTokenRefreshed,
    onRefreshFailed
}: TokenRefreshOptions) {
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isRefreshingRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!refreshToken || isRefreshingRef.current) {
            return;
        }

        isRefreshingRef.current = true;

        try {
            console.log('[TokenRefresh] Refreshing access token...');

            const response = await axios.post(
                `${API_URL}/api/auth/member/refresh`,
                { refreshToken },
                { timeout: 10000 }
            );

            if (response.data.success && response.data.data.accessToken) {
                const newAccessToken = response.data.data.accessToken;
                console.log('[TokenRefresh] Token refreshed successfully');
                onTokenRefreshed(newAccessToken);
            } else {
                throw new Error('Invalid refresh response');
            }
        } catch (error) {
            console.error('[TokenRefresh] Failed to refresh token:', error);
            onRefreshFailed();
        } finally {
            isRefreshingRef.current = false;
        }
    }, [refreshToken, onTokenRefreshed, onRefreshFailed]);

    // Set up automatic refresh
    useEffect(() => {
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Only set up refresh if we have both tokens
        if (!accessToken || !refreshToken) {
            return;
        }

        // Refresh token 1 minute before it expires (14 minutes)
        const refreshInterval = 14 * 60 * 1000; // 14 minutes

        console.log('[TokenRefresh] Scheduling token refresh in 14 minutes');
        refreshTimeoutRef.current = setTimeout(refresh, refreshInterval);

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [accessToken, refreshToken, refresh]);

    // Manual refresh function
    const manualRefresh = useCallback(() => {
        return refresh();
    }, [refresh]);

    return { refresh: manualRefresh };
}

export default useTokenRefresh;
